import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
} from '@mui/material';
import copy from 'clipboard-copy';
import {
  BanIcon,
  CheckIcon,
  CircleCheckIcon,
  CircleXIcon,
  CopyIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  Plus,
  StarIcon,
  Trash2Icon,
} from 'lucide-react';
import {
  type Control,
  type ErrorOption,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';

import { RaLabel } from '@modules/_shared/components/form/RaLabel';
import { useLocale } from '@shared/hooks/locale';

type MultiContactInfoFieldProps = {
  label: string;
  requiredMessage?: string;
  fieldName: 'emails' | 'phone_numbers';
  validateValue?: (
    item: ContactInfoItem,
  ) => Promise<ErrorOption | null> | ErrorOption | null;
  addLabel?: string;
  getSecondaryText?: (item: ContactInfoItem) => ReactNode;
  formatItemValue?: (value: string) => ReactNode;
  UpdateForm: React.FC<{
    control: Control<MultiContactInfoFieldFormData>;
    name: `emails.${number}` | `phone_numbers.${number}`;
  }>;
};

export type ContactInfoItem = {
  id: string;
  value: string;
  verified: boolean;
  primary: boolean;
};

export type MultiContactInfoFieldFormData = {
  emails?: Array<Omit<ContactInfoItem, 'id'>>;
  phone_numbers?: Array<Omit<ContactInfoItem, 'id'>>;
};

type ContactInfoUpdates = {
  primary?: boolean;
  verified?: boolean;
};

type ContactInfoListItemProps = {
  toggleEdit: (id: string) => void;
  contactInfo: ContactInfoItem;
  onDelete: (id: string) => void;
  onUpdate: (id: string, updates: ContactInfoUpdates) => void;
  secondaryText?: ReactNode;
  formatValue?: (value: string) => ReactNode;
};

export const ContactInfoListItem: React.FC<ContactInfoListItemProps> = ({
  toggleEdit,
  contactInfo,
  onDelete,
  onUpdate,
  secondaryText,
  formatValue,
}) => {
  const { t } = useLocale();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const menu = Boolean(menuAnchorEl);

  const toggleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(menuAnchorEl ? null : event.currentTarget);
  };

  return (
    <ListItem
      key={contactInfo.id}
      sx={theme => ({
        backgroundColor: contactInfo.primary
          ? theme.palette.primary.lighter
          : 'none',
      })}
      secondaryAction={
        <IconButton edge="end" onClick={toggleMenu}>
          <EllipsisVerticalIcon size={20} />
        </IconButton>
      }
    >
      <ListItemText
        sx={{ flexGrow: 1 }}
        primary={
          formatValue ? formatValue(contactInfo.value) : contactInfo.value
        }
        secondary={secondaryText}
      />
      {contactInfo.verified && (
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckIcon size={20} />
        </ListItemIcon>
      )}
      <IconButton
        edge="end"
        title={t('copyToClipboard')}
        onClick={() => {
          copy(contactInfo.value);
        }}
      >
        <CopyIcon size={20} />
      </IconButton>
      <Menu
        anchorEl={menuAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={menu}
        onClose={toggleMenu}
      >
        {contactInfo.primary !== true && (
          <MenuItem
            onClick={(evt: React.MouseEvent<HTMLElement>) => {
              onUpdate(contactInfo.id, { primary: true });
              toggleMenu(evt);
            }}
          >
            <ListItemIcon>
              <StarIcon size={18} />
            </ListItemIcon>
            {t('Mark as primary')}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            onUpdate(contactInfo.id, {
              verified: contactInfo.verified ? false : true,
            });
          }}
        >
          <ListItemIcon>
            {contactInfo.verified ? (
              <BanIcon size={18} />
            ) : (
              <CheckIcon size={18} />
            )}
          </ListItemIcon>
          {contactInfo.verified
            ? t('Mark as unverified')
            : t('Mark as verified')}
        </MenuItem>
        <MenuItem onClick={() => toggleEdit(contactInfo.id)}>
          <ListItemIcon>
            <PencilIcon size={18} />
          </ListItemIcon>
          {t('Edit')}
        </MenuItem>
        <MenuItem onClick={() => onDelete(contactInfo.id)}>
          <ListItemIcon>
            <Trash2Icon size={18} />
          </ListItemIcon>
          {t('Delete')}
        </MenuItem>
      </Menu>
    </ListItem>
  );
};

type ContactInfoFieldFormProps = React.PropsWithChildren<{
  onCancel?: () => void;
  onSubmit: () => void;
}>;

const ContactInfoFieldForm: React.FC<ContactInfoFieldFormProps> = ({
  onCancel,
  onSubmit,
  children,
}) => {
  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center', px: 1 }}>
      {children}
      <Stack
        sx={{
          justifyContent: 'flex-end',
        }}
      >
        <IconButton color="success" onClick={onSubmit}>
          <CircleCheckIcon size={20} />
        </IconButton>
        {onCancel && (
          <IconButton onClick={onCancel} color="error">
            <CircleXIcon size={20} />
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
};

export const MultiContactInfoField: React.FC<MultiContactInfoFieldProps> = ({
  label,
  requiredMessage,
  fieldName,
  validateValue,
  addLabel,
  UpdateForm,
  getSecondaryText,
  formatItemValue,
}) => {
  const { t } = useLocale();
  const isFirstRender = useRef(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const {
    control,
    trigger,
    getValues,
    setError,
    register,
    formState: { errors, isDirty, isSubmitted },
  } = useFormContext<MultiContactInfoFieldFormData>();
  const { fields, remove, append, update, replace } =
    useFieldArray<MultiContactInfoFieldFormData>({
      name: fieldName,
    });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // In case of reset (of the form), we need to reset the adding and editing states.
    if (!isDirty && !isSubmitted) {
      setIsAdding(false);
      setEditing(null);
    }
  }, [isDirty, isSubmitted]);

  const onUpdate = useCallback(
    async (id: string, updates: ContactInfoUpdates) => {
      const contactInfoIndex = fields.findIndex(field => field.id === id);
      if (contactInfoIndex === -1) {
        return;
      }

      if (updates.primary === true) {
        // If we make a contact info primary, we need to make all other contact infos non-primary.
        replace(
          fields.map(field => ({
            ...field,
            primary: field.id === id,
          })),
        );
      } else {
        update(contactInfoIndex, {
          ...fields[contactInfoIndex],
          ...updates,
        });
      }
    },
    [fields, update, replace],
  );

  const onDelete = useCallback(
    async (id: string) => {
      const contactInfoIndex = fields.findIndex(field => field.id === id);
      if (contactInfoIndex === -1) {
        return;
      }

      remove(contactInfoIndex);
    },
    [fields, remove],
  );

  const onCancel = useMemo(
    () =>
      (index: number): (() => void) =>
      () => {
        if (isAdding) {
          remove(index);
        }

        setIsAdding(false);
        setEditing(null);
      },
    [isAdding, remove],
  );

  useEffect(() => {
    // useFieldArray minLength rule isn’t enforced on RaForm submit because RHF doesn't validate the array root on submit unless the field is registered. So we have to explicitly register the array root with a validator enforcing min length.
    if (requiredMessage == null) {
      return;
    }

    register(fieldName, {
      validate: val => {
        if (val == null || val.length === 0) {
          return requiredMessage;
        }
      },
    });
  }, [register, fieldName, requiredMessage, t]);

  return (
    <FormControl fullWidth component="fieldset" error={!!errors[fieldName]}>
      <RaLabel label={label} required={!!requiredMessage} />
      {fields.length > 0 && (
        <List sx={{ backgroundColor: 'background.paper' }}>
          {fields.map((field, index) =>
            editing !== index ? (
              <ContactInfoListItem
                key={field.id}
                contactInfo={field}
                toggleEdit={() => setEditing(index)}
                onDelete={onDelete}
                onUpdate={onUpdate}
                secondaryText={getSecondaryText?.(field)}
                formatValue={formatItemValue}
              />
            ) : (
              <ContactInfoFieldForm
                key={field.id}
                onCancel={onCancel(index)}
                onSubmit={() => {
                  const onSubmit = async () => {
                    const valid = await trigger(`${fieldName}.${index}`);
                    if (!valid) {
                      return;
                    }

                    const updatedContactInfo = getValues(
                      `${fieldName}.${index}`,
                    );

                    if (validateValue != null) {
                      const error = await validateValue({
                        id: field.id,
                        ...updatedContactInfo,
                      });

                      if (error != null) {
                        setError(`${fieldName}.${index}.value`, error);
                        return;
                      }
                    }

                    // We need to update fields because even though the internal state is updated, ListItem do not have the correct data otherwise.
                    update(index, updatedContactInfo);
                    setEditing(null);
                    setIsAdding(false);
                  };

                  onSubmit();
                }}
              >
                <UpdateForm control={control} name={`${fieldName}.${index}`} />
              </ContactInfoFieldForm>
            ),
          )}
        </List>
      )}
      {editing == null && (
        <Stack sx={{ mt: 1 }}>
          <Button
            color="primary"
            startIcon={<Plus />}
            onClick={() => {
              append({ value: '', verified: false, primary: false });
              setIsAdding(true);
              setEditing(fields.length);
            }}
          >
            {addLabel ?? t('Add')}
          </Button>
        </Stack>
      )}
      {errors[fieldName]?.root?.message && (
        <FormHelperText>{errors[fieldName].root.message}</FormHelperText>
      )}
    </FormControl>
  );
};
